<template>
  <Teleport to="body">
    <div
      v-show="visible"
      ref="boxRef"
      class="halo-ai-assistant__box"
      :style="boxStyle"
    >
      <!-- 四边 resize handle -->
      <div
        class="halo-ai-resize-handle halo-ai-resize-handle--top"
        @mousedown.stop="onResize('top', $event)"
      ></div>
      <div
        class="halo-ai-resize-handle halo-ai-resize-handle--right"
        @mousedown.stop="onResize('right', $event)"
      ></div>
      <div
        class="halo-ai-resize-handle halo-ai-resize-handle--bottom"
        @mousedown.stop="onResize('bottom', $event)"
      ></div>
      <div
        class="halo-ai-resize-handle halo-ai-resize-handle--left"
        @mousedown.stop="onResize('left', $event)"
      ></div>

      <div
        class="halo-ai-assistant__header"
        @mousedown="onMousedown"
      >
        <div class="halo-ai-assistant__title-wrapper">
          <span class="halo-ai-assistant__title">AI小智</span>
          <el-tag size="small">Beta</el-tag>
        </div>
        <div class="halo-ai-assistant__actions">
          <!-- 缩小 -->
          <span
            class="halo-ai-assistant__btn"
            :class="{ 'is-active': boxWidth === MIN_WIDTH }"
            title="缩小"
            @mousedown.stop
            @click="shrink"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="4 14 10 14 10 20" />
              <polyline points="20 10 14 10 14 4" />
              <line
                x1="10"
                y1="14"
                x2="3"
                y2="21"
              />
              <line
                x1="21"
                y1="3"
                x2="14"
                y2="10"
              />
            </svg>
          </span>
          <!-- 放大 -->
          <span
            class="halo-ai-assistant__btn"
            :class="{ 'is-active': boxWidth === MAX_WIDTH }"
            title="放大"
            @mousedown.stop
            @click="expand"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line
                x1="21"
                y1="3"
                x2="14"
                y2="10"
              />
              <line
                x1="3"
                y1="21"
                x2="10"
                y2="14"
              />
            </svg>
          </span>
          <!-- 隐藏 -->
          <span
            class="halo-ai-assistant__btn"
            title="隐藏"
            @mousedown.stop
            @click="hide"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              />
            </svg>
          </span>
        </div>
      </div>
      <div class="halo-ai-assistant__body">
        <iframe
          v-if="preloaded"
          ref="iframeRef"
          :src="iframeSrc"
          width="100%"
          height="100%"
          frameborder="0"
          allowfullscreen
          title="AI小智"
        ></iframe>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';

  const iframeSrc = import.meta.env.VITE_APP_AI_ASSISTANT_URL || '';
  const iframeRef = ref<HTMLIFrameElement | null>(null);

  const MIN_WIDTH = 460;
  const MIN_HEIGHT = 600;
  const MAX_WIDTH = 760;
  const getMaxHeight = () => window.innerHeight * 0.8;

  const visible = ref(false);
  // 预加载：组件挂载后立即设置 src，不等到 open()
  const preloaded = ref(false);

  onMounted(() => {
    if (iframeSrc) {
      preloaded.value = true;
    }
  });
  const boxWidth = ref(MIN_WIDTH);
  const boxHeight = ref(MIN_HEIGHT);
  const posX = ref(0);
  const posY = ref(0);
  const boxRef = ref<HTMLElement | null>(null);

  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

  const boxStyle = computed(() => ({
    width: `${boxWidth.value}px`,
    height: `${boxHeight.value}px`,
    left: `${posX.value}px`,
    top: `${posY.value}px`,
  }));

  const resetPosition = () => {
    boxWidth.value = MIN_WIDTH;
    boxHeight.value = MIN_HEIGHT;
    // 确保弹窗在视口内
    posX.value = Math.max(0, window.innerWidth - MIN_WIDTH - 20);
    posY.value = Math.max(0, window.innerHeight * 0.1);

    // 如果位置仍然超出视口，使用更保守的位置
    if (posX.value + MIN_WIDTH > window.innerWidth) {
      posX.value = Math.max(0, window.innerWidth - MIN_WIDTH - 10);
    }
  };

  const constrainPosition = () => {
    posX.value = clamp(posX.value, 0, window.innerWidth - boxWidth.value);
    posY.value = clamp(posY.value, 0, window.innerHeight - boxHeight.value);
  };

  const open = () => {
    if (!iframeSrc) {
      console.warn('AI小智URL未配置，请检查VITE_APP_AI_ASSISTANT_URL环境变量');
      return;
    }
    resetPosition();
    visible.value = true;
  };

  const hide = () => { visible.value = false; };

  const expand = () => {
    boxWidth.value = MAX_WIDTH;
    boxHeight.value = getMaxHeight();
    constrainPosition();
  };

  const shrink = () => {
    boxWidth.value = MIN_WIDTH;
    boxHeight.value = MIN_HEIGHT;
    constrainPosition();
  };

  // 拖拽/resize 时给 iframe 盖一层透明遮罩，防止 iframe 吞掉鼠标事件
  const setIframeMask = (active: boolean) => {
    if (!boxRef.value) { return; }
    const existing = boxRef.value.querySelector<HTMLElement>('.halo-ai-iframe-mask');
    if (active) {
      if (existing) { return; }
      const mask = document.createElement('div');
      mask.className = 'halo-ai-iframe-mask';
      mask.style.cssText = 'position:absolute;inset:0;z-index:10;';
      boxRef.value.appendChild(mask);
    } else {
      existing?.remove();
    }
  };

  const onMousedown = (e: MouseEvent) => {
    // 防止事件冒泡
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX - posX.value;
    const startY = e.clientY - posY.value;

    // 使用requestAnimationFrame优化性能
    let animationId: number | null = null;

    const onMousemove = (ev: MouseEvent) => {
      // 取消之前的动画帧
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }

      animationId = requestAnimationFrame(() => {
        posX.value = clamp(ev.clientX - startX, 0, window.innerWidth - boxWidth.value);
        posY.value = clamp(ev.clientY - startY, 0, window.innerHeight - boxHeight.value);
      });
    };

    const cleanup = () => {
      // 清理动画帧
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }

      setIframeMask(false);

      // 移除事件监听器
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('mouseup', cleanup);

      // 恢复文本选择
      document.body.style.userSelect = '';
    };

    // 防止文本选择
    document.body.style.userSelect = 'none';
    setIframeMask(true);

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', cleanup);

    // 添加全局mouseup监听，确保鼠标在任何地方释放都能清理
    window.addEventListener('mouseup', cleanup);
  };

  type ResizeDirection = 'top' | 'right' | 'bottom' | 'left';

  const onResize = (direction: ResizeDirection, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const maxHeight = getMaxHeight();

    /*
     * 锚点：拖左边时右边缘固定，拖上边时底边缘固定
     * 每帧直接用鼠标当前位置计算，不做增量累加，彻底避免漂移
     */
    const rightEdge = posX.value + boxWidth.value; // 拖 left 时固定
    const bottomEdge = posY.value + boxHeight.value; // 拖 top 时固定
    const leftEdge = posX.value; // 拖 right 时固定
    const topEdge = posY.value; // 拖 bottom 时固定

    const onMousemove = (ev: MouseEvent) => {
      switch (direction) {
      case 'right': {
        // 鼠标 X 就是新右边缘，宽度 = 鼠标X - 左边缘
        const newWidth = clamp(ev.clientX - leftEdge, MIN_WIDTH, MAX_WIDTH);
        boxWidth.value = newWidth;
        break;
      }
      case 'left': {
        // 鼠标 X 就是新左边缘，宽度 = 右边缘 - 鼠标X
        const newWidth = clamp(rightEdge - ev.clientX, MIN_WIDTH, MAX_WIDTH);
        boxWidth.value = newWidth;
        // 左边缘 = 右边缘 - 实际宽度（宽度被 clamp 后左边缘要跟着修正）
        posX.value = clamp(rightEdge - newWidth, 0, rightEdge - MIN_WIDTH);
        break;
      }
      case 'bottom': {
        // 鼠标 Y 就是新底边缘，高度 = 鼠标Y - 顶边缘
        const newHeight = clamp(ev.clientY - topEdge, MIN_HEIGHT, maxHeight);
        boxHeight.value = newHeight;
        break;
      }
      case 'top': {
        // 鼠标 Y 就是新顶边缘，高度 = 底边缘 - 鼠标Y
        const newHeight = clamp(bottomEdge - ev.clientY, MIN_HEIGHT, maxHeight);
        boxHeight.value = newHeight;
        // 顶边缘 = 底边缘 - 实际高度
        posY.value = clamp(bottomEdge - newHeight, 0, bottomEdge - MIN_HEIGHT);
        break;
      }
      default:
        break;
      }
    };

    const cleanup = () => {
      setIframeMask(false);
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('mouseup', cleanup);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    setIframeMask(true);
    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('mouseup', cleanup);
  };

  defineExpose({ open });
</script>

<style lang="scss">
.halo-ai-assistant__box {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  // 硬件加速

  .halo-ai-assistant__header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 6px 16px;
    background: linear-gradient(90deg, #e8f4ff 0%, #f0f7ff 100%);
    border-bottom: 1px solid #e4e7ed;
    cursor: move;
    user-select: none;
  }

  .halo-ai-assistant__title {
    color: #303133;
    font-weight: 600;
    font-size: 14px;
  }

  .halo-ai-assistant__title-wrapper {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .halo-ai-assistant__actions {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .halo-ai-assistant__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: #606266;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s,color 0.2s;

    &:hover {
      color: #303133;
      background: rgba(0, 0, 0, 0.06);
    }

    &.is-active {
      color: #409eff;
    }
  }

  .halo-ai-assistant__body {
    flex: 1;
    overflow: hidden;

    iframe {
      width: 100%;
      height: 100%;
      background: transparent;
      border-width: 0;
    }
  }
}

.halo-ai-resize-handle {
  position: absolute;
  z-index: 1;

  &--top {
    top: -3px;
    right: 8px;
    left: 8px;
    height: 6px;
    cursor: n-resize;
  }

  &--right {
    top: 8px;
    right: -3px;
    bottom: 8px;
    width: 6px;
    cursor: e-resize;
  }

  &--bottom {
    right: 8px;
    bottom: -3px;
    left: 8px;
    height: 6px;
    cursor: s-resize;
  }

  &--left {
    top: 8px;
    bottom: 8px;
    left: -3px;
    width: 6px;
    cursor: w-resize;
  }
}
</style>
